import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Box, Tabs, Tab, Typography, CircularProgress } from '@mui/material';
import { useAuthTenant, useAuthLoggedIn } from 'store/auth';
import { getProtectedTransparency } from 'services/transparency-service';
import { Container } from 'Components/Container';
import { OverviewTab } from './components/OverviewTab';
import { FrameworksTab } from './components/FrameworksTab';
import { GovernanceStructure } from './components/GovernanceStructure';
import { PoliciesTab } from './components/PoliciesTab';
import { ExternalViewerContainer, TabContent } from './styles';

/**
 * ProtectedTransparencyView
 * - Requires JWT (internal user) OR access token (external viewer)
 * - Token passed via ?token=xxx query param or X-Access-Token header
 * - Shows detailed governance information with drill-down
 */
export const ProtectedTransparencyView = ({
  companyKey: companyKeyProp,
  accessToken: accessTokenProp,
  isExternalViewer: isExternalViewerProp = false,
}) => {
  const [searchParams] = useSearchParams();
  const accessToken = accessTokenProp || searchParams.get('token');
  const companyKeyParam = searchParams.get('companyKey');

  const isLoggedIn = useSelector(useAuthLoggedIn);
  const selectedTenant = useSelector(useAuthTenant);

  // Determine company key: from prop, from query param, or from Redux (logged-in user)
  const companyKey = companyKeyProp || companyKeyParam || selectedTenant?.slug;

  const [activeTab, setActiveTab] = useState(0);

  const { isLoading, error, data } = useQuery(
    ['protectedTransparency', companyKey, accessToken],
    () => getProtectedTransparency(companyKey, accessToken),
    { enabled: !!companyKey }
  );

  // External viewer = has token but not logged in, or explicitly set
  const isExternalViewer = isExternalViewerProp || (!!accessToken && !isLoggedIn);

  const responseData = data?.data;

  // SEO: Prevent indexing (Story-008 requirement)
  // Must be called before any early returns to follow React hooks rules
  useEffect(() => {
    document.title = `Centro de Transparência - ${responseData?.organization?.name || ''}`;
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, [responseData?.organization?.name]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !responseData) {
    const errorContent = (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography variant="h5" color="text.secondary">
          Erro ao carregar dados de transparência.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Verifique se o token de acesso é válido ou entre em contato com o administrador.
        </Typography>
      </Box>
    );

    if (isExternalViewer) {
      return <ExternalViewerContainer>{errorContent}</ExternalViewerContainer>;
    }

    return (
      <Container title="Centro de Transparência">
        {errorContent}
      </Container>
    );
  }

  const { organization, frameworks, governanceStructure, policies } = responseData;

  const tabContent = (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Visão Geral" />
          <Tab label="Frameworks" />
          <Tab label="Governança" />
          <Tab label="Políticas" />
        </Tabs>
      </Box>

      <TabContent>
        {activeTab === 0 && (
          <OverviewTab
            organization={organization}
            frameworks={frameworks}
            governanceStructure={governanceStructure}
          />
        )}
        {activeTab === 1 && (
          <FrameworksTab
            frameworks={frameworks}
            companyKey={companyKey}
            accessToken={accessToken}
          />
        )}
        {activeTab === 2 && (
          <GovernanceStructure
            categories={governanceStructure?.categories}
            overallPosture={governanceStructure?.overallPosture}
            showSubDomains={true}
          />
        )}
        {activeTab === 3 && (
          <PoliciesTab
            policies={policies}
            companyKey={companyKey}
            accessToken={accessToken}
          />
        )}
      </TabContent>
    </>
  );

  // External viewer: minimal layout (no sidebar/header)
  if (isExternalViewer) {
    return (
      <ExternalViewerContainer>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Centro de Transparência - {organization?.name}
        </Typography>
        {tabContent}
      </ExternalViewerContainer>
    );
  }

  // Internal user: standard VO layout
  return (
    <Container
      title="Centro de Transparência"
      breadcrumb="Relatórios / Centro de Transparência"
    >
      {tabContent}
    </Container>
  );
};
